import { create } from "zustand";

import { Artefact, ArtefactTab } from "@/types/artefacts";
import { Citation } from "@/types/citations";
import { logger } from "@/telemetry";

export type ArtefactSource = "sse-streaming" | "history-load" | "regeneration";

export interface AddArtefactOptions {
  source?: ArtefactSource;
  autoShow?: boolean;
}

export interface ArtefactStoreState {
  // Artefacts state
  artefactsVisible: boolean;
  activeArtefactTab: ArtefactTab;
  artefactsByMessage: Map<string, Artefact[]>;
  citationsByMessage: Map<string, Citation[]>;
  selectedArtefactIds: string[];
  messageArtefactMap: Map<string, string[]>;
  autoShowSuppressed: boolean;

  // Artefacts actions
  toggleArtefacts: () => void;
  setActiveArtefactTab: (tab: ArtefactTab) => void;
  addArtefact: (artefact: Artefact, options?: AddArtefactOptions) => void;
  getArtefactsForCurrentChat: () => Artefact[];
  getFilteredArtefactsForCurrentChat: () => Artefact[];
  clearArtefacts: () => void;
  clearAllState: () => void;

  // Selection actions
  selectArtefact: (artefactId: string) => void;
  selectMultipleArtefacts: (artefactIds: string[]) => void;
  clearArtefactSelection: () => void;
  toggleArtefactSelection: (artefactId: string) => void;

  // Message-to-artefact mapping actions
  registerMessageArtefacts: (messageId: string, artefactIds: string[]) => void;
  getMessageArtefacts: (messageId: string) => string[];

  // Composite navigation action
  navigateToArtefact: (messageId: string) => void;

  // Citations actions
  addCitations: (messageId: string, citations: Citation[]) => void;
  getCitationsForCurrentChat: () => Citation[];
  clearCitations: () => void;
}

/** Whether a message already holds an artefact of the same type with identical content. */
function hasSameTypeAndContent(messageArtefacts: Artefact[], artefact: Artefact): boolean {
  return messageArtefacts.some((a) => a.type === artefact.type && a.content === artefact.content);
}

/** Auto-show the panel on desktop only (not mobile <640px). */
const MOBILE_BREAKPOINT_PX = 640;

/**
 * Enhanced auto-show logic based on source: whether a newly added artefact
 * should open the panel, and whether it should be selected.
 */
function decideArtefactReveal(
  state: ArtefactStoreState,
  isNewArtefact: boolean,
  options: AddArtefactOptions | undefined,
): { shouldAutoShow: boolean; shouldSelectArtefact: boolean } {
  const { source, autoShow } = options || {};
  const mayAutoShow = autoShow !== false && !state.autoShowSuppressed;

  if (isNewArtefact && (source === "sse-streaming" || source === "regeneration")) {
    // For SSE streaming or regeneration, always select the artefact
    // so it's highlighted when user manually opens panel
    const isMobileScreen =
      typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT_PX;

    return {
      shouldAutoShow: mayAutoShow && !state.artefactsVisible && !isMobileScreen,
      shouldSelectArtefact: true,
    };
  }

  // Panel is closed and this is a new artefact. Never auto-show for
  // history-loaded artefacts; with no source (backward compatibility),
  // auto-show only for the first artefact.
  const shouldAutoShow =
    mayAutoShow &&
    !state.artefactsVisible &&
    isNewArtefact &&
    source === undefined &&
    state.getArtefactsForCurrentChat().length === 1;

  return { shouldAutoShow, shouldSelectArtefact: false };
}

const useArtefactStore = create<ArtefactStoreState>((set, get) => ({
  // Artefacts state with localStorage persistence
  artefactsVisible: (() => {
    try {
      return localStorage.getItem("artefactsVisible") === "true";
    } catch {
      return false;
    }
  })(),
  activeArtefactTab: (() => {
    try {
      const stored = localStorage.getItem("activeArtefactTab") as ArtefactTab;

      return stored === "citations" || stored === "generated" ? stored : "generated";
    } catch {
      return "generated";
    }
  })(),
  artefactsByMessage: new Map<string, Artefact[]>(),
  citationsByMessage: new Map<string, Citation[]>(),
  selectedArtefactIds: [],
  messageArtefactMap: new Map<string, string[]>(),
  autoShowSuppressed: false,

  // Artefacts actions implementation
  toggleArtefacts: () => {
    const wasVisible = get().artefactsVisible;
    const isClosing = wasVisible;
    const newState = !wasVisible;

    // When user closes the panel, suppress auto-show for the rest of this chat.
    // When user opens the panel, clear the suppression.
    set({ artefactsVisible: newState, autoShowSuppressed: isClosing });
    try {
      localStorage.setItem("artefactsVisible", String(newState));
    } catch (error) {
      logger.warn("Failed to persist artefacts visibility", { error: String(error) });
    }
  },

  setActiveArtefactTab: (tab: ArtefactTab) => {
    set({ activeArtefactTab: tab });
    try {
      localStorage.setItem("activeArtefactTab", tab);
    } catch (error) {
      logger.warn("Failed to persist active artefact tab", { error: String(error) });
    }
  },

  addArtefact: (artefact: Artefact, options?: AddArtefactOptions) => {
    const currentArtefacts = get().artefactsByMessage;
    const messageArtefacts = currentArtefacts.get(artefact.messageId) || [];

    // Check if artefact already exists (by ID)
    const existingIndex = messageArtefacts.findIndex((a) => a.id === artefact.id);
    const isNewArtefact = existingIndex === -1;

    // Content-based dedup: if an artefact with the same type and identical
    // content already exists for this message (under a different ID), treat
    // it as a duplicate and skip the add.  This prevents the same graph
    // appearing twice when the LLM includes graph JSON in the response body
    // AND the tool_call_executions side-channel.
    if (isNewArtefact && hasSameTypeAndContent(messageArtefacts, artefact)) {
      logger.info("Skipping duplicate artefact (same type + content)", {
        artefactId: artefact.id,
        type: artefact.type,
        messageId: artefact.messageId,
      });

      return;
    }

    const updatedArtefacts = new Map(currentArtefacts);

    if (isNewArtefact) {
      updatedArtefacts.set(artefact.messageId, [...messageArtefacts, artefact]);
    } else {
      // Update existing artefact
      messageArtefacts[existingIndex] = artefact;
      updatedArtefacts.set(artefact.messageId, [...messageArtefacts]);
    }

    set({ artefactsByMessage: updatedArtefacts });

    const currentState = get();
    const { shouldAutoShow, shouldSelectArtefact } = decideArtefactReveal(
      currentState,
      isNewArtefact,
      options,
    );

    if (shouldAutoShow) {
      currentState.toggleArtefacts();
    }

    // Switch to generated tab and select artefact for SSE streaming/regeneration
    if (shouldSelectArtefact) {
      const latestState = get(); // Get fresh state after potential toggle

      if (latestState.activeArtefactTab !== "generated") {
        latestState.setActiveArtefactTab("generated");
      }
      // Call selectArtefact to expand accordion
      latestState.selectArtefact(artefact.id);
    }
  },

  getArtefactsForCurrentChat: () => {
    const artefactsByMessage = get().artefactsByMessage;
    const allArtefacts: Artefact[] = [];

    artefactsByMessage.forEach((artefacts) => {
      allArtefacts.push(...artefacts);
    });

    // Sort by timestamp (newest first)
    return allArtefacts.sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  },

  getFilteredArtefactsForCurrentChat: () => {
    // Simply return all artefacts - no filtering
    return get().getArtefactsForCurrentChat();
  },

  clearArtefacts: () => {
    set({
      artefactsByMessage: new Map<string, Artefact[]>(),
      autoShowSuppressed: false,
    });
  },

  clearAllState: () => {
    // Clear all in-memory state
    set({
      artefactsVisible: false,
      activeArtefactTab: "generated",
      artefactsByMessage: new Map<string, Artefact[]>(),
      citationsByMessage: new Map<string, Citation[]>(),
      selectedArtefactIds: [],
      messageArtefactMap: new Map<string, string[]>(),
      autoShowSuppressed: false,
    });

    // Clear all localStorage persistence
    try {
      localStorage.removeItem("artefactsVisible");
      localStorage.removeItem("activeArtefactTab");
      localStorage.removeItem("chatPanelWidth"); // Clear any saved panel width
      logger.info("🧹 ArtefactStore: Cleared all state including localStorage");
    } catch (error) {
      logger.warn("Failed to clear ArtefactStore localStorage", { error: String(error) });
    }
  },

  // Selection actions implementation
  selectArtefact: (artefactId: string) => {
    set({ selectedArtefactIds: [artefactId] });
  },

  selectMultipleArtefacts: (artefactIds: string[]) => {
    set({ selectedArtefactIds: artefactIds });
  },

  clearArtefactSelection: () => {
    set({ selectedArtefactIds: [] });
  },

  toggleArtefactSelection: (artefactId: string) => {
    const { selectedArtefactIds } = get();
    const isSelected = selectedArtefactIds.includes(artefactId);

    set({
      selectedArtefactIds: isSelected
        ? selectedArtefactIds.filter((id) => id !== artefactId)
        : [...selectedArtefactIds, artefactId],
    });
  },

  // Message-to-artefact mapping actions implementation
  registerMessageArtefacts: (messageId: string, artefactIds: string[]) => {
    const { messageArtefactMap } = get();
    const updatedMap = new Map(messageArtefactMap);

    updatedMap.set(messageId, artefactIds);
    set({ messageArtefactMap: updatedMap });
  },

  getMessageArtefacts: (messageId: string) => {
    const { messageArtefactMap } = get();

    return messageArtefactMap.get(messageId) || [];
  },

  // Composite navigation action implementation
  navigateToArtefact: (messageId: string) => {
    const state = get();
    const artefactIds = state.getMessageArtefacts(messageId);

    // No artefacts to navigate to
    if (artefactIds.length === 0) return;

    // User is explicitly navigating — always clear auto-show suppression
    set({ autoShowSuppressed: false });

    // 1. Open panel if closed
    if (!state.artefactsVisible) {
      state.toggleArtefacts();
    }

    // 2. Switch to "generated" tab if on wrong tab
    if (state.activeArtefactTab !== "generated") {
      state.setActiveArtefactTab("generated");
    }

    // 3. Select all artefacts from this message (expands accordion)
    state.selectMultipleArtefacts(artefactIds);
  },

  // Citations actions
  addCitations: (messageId: string, citations: Citation[]) => {
    if (!citations || citations.length === 0) return;

    const currentCitations = get().citationsByMessage;
    const updatedCitations = new Map(currentCitations);

    updatedCitations.set(messageId, citations);
    set({ citationsByMessage: updatedCitations });

    // Auto-show panel if citations exist and panel is not visible and no artefacts exist
    // Respect user's choice to hide the panel for this chat session
    const allCitations = get().getCitationsForCurrentChat();
    const allArtefacts = get().getArtefactsForCurrentChat();

    if (
      !get().artefactsVisible &&
      !get().autoShowSuppressed &&
      allCitations.length > 0 &&
      allArtefacts.length === 0
    ) {
      get().toggleArtefacts();
    }
  },

  getCitationsForCurrentChat: () => {
    const citationsByMessage = get().citationsByMessage;
    const allCitations: Citation[] = [];

    citationsByMessage.forEach((citations) => {
      allCitations.push(...citations);
    });

    return allCitations;
  },

  clearCitations: () => {
    set({ citationsByMessage: new Map<string, Citation[]>() });
  },
}));

export { useArtefactStore };
